/**
 * Hybrid FormField Component
 * Automatically chooses between Tailwind and styled-components based on props.
 *
 * FormField's intrinsic props (notably `mb`, which always has a default and is
 * itself a styled-system prop) MUST be destructured out before the routing
 * check — otherwise hasStyledProps would always be true and the Tailwind path
 * would be dead code.
 */

import React from "react";
import { needsStyledComponents } from "@sproutsocial/seeds-react-system-props";
import type { TypeFormFieldProps } from "./FormFieldTypes";
import FormFieldStyled from "./FormFieldStyled";
import { FormFieldTailwind } from "./FormFieldTailwind";

// styled-components injects a generated `sc-<hash>` className on extended
// components. Detect it so `styled(FormField)` extensions render via the SC
// (Box) path even when no ThemeProvider is in scope (no `theme`/
// `forwardedComponent` prop is passed in that case).
const SC_CLASS_PATTERN = /\bsc-[a-zA-Z0-9]+\b/;

const FormFieldHybrid = React.forwardRef<HTMLDivElement, TypeFormFieldProps>(
  (props, ref) => {
    const {
      children,
      error,
      helperText,
      id,
      isInvalid,
      label,
      qa,
      isLabelHidden,
      required,
      mb,
      ...rest
    } = props;

    // The Tailwind path maps a scalar mb to an inline space token; a responsive
    // mb (array/object) must go through Box on the styled path.
    const mbIsResponsive =
      mb !== undefined && typeof mb !== "number" && typeof mb !== "string";

    const isScExtension = SC_CLASS_PATTERN.test(props.className ?? "");

    if (needsStyledComponents(rest) || mbIsResponsive || isScExtension) {
      return <FormFieldStyled {...props} ref={ref} />;
    }

    return <FormFieldTailwind {...props} ref={ref} />;
  }
);

FormFieldHybrid.displayName = "FormField";
export default FormFieldHybrid;
